import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ErrorMessage } from '../../components/common';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import BackButton from '../../components/common/BackButton';
import ShippingSection from '../../components/checkout/ShippingSection';
import PaymentSection from '../../components/checkout/PaymentSection';
import OrderSummary from '../../components/checkout/OrderSummary';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-hot-toast';

import { 
  selectAllCartItems,
  clearCartState
} from '../../redux/slices/cartSlice';
import { setCurrentOrder } from '../../redux/slices/orderSlice';
import { 
  setOrderId, 
  resetCheckout, 
  setCheckoutCurrency 
} from '../../redux/slices/checkoutSlice';
import { useCreateOrderMutation } from '../../redux/apis/endpoints/orderApi';
import { useRemovePurchasedItemsFromCartMutation } from '../../redux/apis/endpoints/paymentApi';

// Load Stripe with your publishable key (replace with your actual key)
// console.log('Stripe publishable key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const stripePromise = loadStripe(stripePublishableKey);

const CheckoutPage = () => {
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [stripeElementsKey, setStripeElementsKey] = useState(1);
  
  const memoizedStripePromise = useMemo(() => {
    return stripePromise;
  }, [stripeElementsKey]);
  
  const history = useHistory();
  const dispatch = useDispatch();

  const [createOrderMutation, { isLoading: isCreatingOrder, error: createOrderApiError }] = useCreateOrderMutation();
  const [removePurchasedItemsFromCart, { isLoading: isRemovingItems }] = useRemovePurchasedItemsFromCartMutation();
  
  const allCartItemsFromStore = useSelector(selectAllCartItems);
  
  // Get the checkout currency from localStorage
  // const checkoutCurrency = localStorage.getItem('checkoutCurrency') || 'USD';
  // console.log(`Checkout page initializing with currency: ${checkoutCurrency}`);

  const checkoutCurrencyFromStorage = localStorage.getItem('checkoutCurrency');
  // console.log('[CheckoutPage] Currency read from localStorage:', checkoutCurrencyFromStorage);
  const checkoutCurrency = checkoutCurrencyFromStorage || 'USD';
  // console.log(`[CheckoutPage] Effective checkoutCurrency to be used: ${checkoutCurrency}`);
  
  // Filter cart items by currency
  const cartItems = useMemo(() => {
    const currencyUpperCase = checkoutCurrency.toUpperCase();
    
    const filteredItems = allCartItemsFromStore.filter(item => {
      const itemCurrency = (item.currency || 'USD').toUpperCase();
      return itemCurrency === currencyUpperCase;
    });
    
    console.log(`Filtered ${filteredItems.length} items with currency ${checkoutCurrency} from ${allCartItemsFromStore.length} total items`);
    
    if (filteredItems.length === 0 && allCartItemsFromStore.length > 0) {
      console.warn(`No items found with currency ${checkoutCurrency}. Available currencies:`, 
        [...new Set(allCartItemsFromStore.map(item => (item.currency || 'USD').toUpperCase()))]);
    }
    
    return filteredItems;
  }, [allCartItemsFromStore, checkoutCurrency]);
  
  const checkoutState = useSelector(state => state.checkout);
  const { user } = useSelector(state => state.auth);
  const shippingComplete = useSelector(state => state.checkout.shippingComplete);
  const strapiPaymentIdFromState = useSelector(state => state.checkout.strapiPaymentId); // Get strapiPaymentId
  
  // Initialize checkout currency in Redux state
  useEffect(() => {
    dispatch(setCheckoutCurrency(checkoutCurrency));
  }, [dispatch, checkoutCurrency]);
  
  // Redirect to cart if no items match the selected currency
  useEffect(() => {
    if (cartItems.length === 0) {
      console.log('No items found for currency, redirecting to cart');
      history.push('/cart');
    }
  }, [cartItems, history]);
  
  useEffect(() => {
    if (!user) {
      history.push('/login?redirect=checkout');
    }
  }, [user, history]);

  const handleShippingComplete = (isComplete) => {
    // console.log("Shipping completion status from component:", isComplete);
  };

  const handlePaymentComplete = async (stripePaymentIntentId, receivedStrapiPaymentId) => { 
    console.log('[CheckoutPage] Payment successful. Stripe PI ID:', stripePaymentIntentId, 'Strapi Payment ID:', receivedStrapiPaymentId || strapiPaymentIdFromState);
    setPageLoading(true); 
    setPageError(null);
    
    const finalStrapiPaymentId = receivedStrapiPaymentId || strapiPaymentIdFromState;

    if (!finalStrapiPaymentId) {
      console.error('[CheckoutPage] Strapi Payment ID is missing. Cannot proceed with order creation.');
      setPageError('Critical error: Payment session identifier is missing. Cannot create order.');
      setPageLoading(false);
      toast.error('Critical error: Payment session identifier is missing.');
      return;
    }

    try {
      // Order data payload is now much simpler. Backend uses strapiPaymentId to get all details.
      const orderDataPayload = {
        strapiPaymentId: finalStrapiPaymentId,
        // notes: clientNotes, // If you have a way to collect client notes, include them here.
        // The backend will derive currency, items, amounts, shippingAddressId etc., from the Payment record.
      };
      
      console.log('[CheckoutPage] Order data payload for createOrderMutation:', orderDataPayload);

      const createdOrder = await createOrderMutation(orderDataPayload).unwrap();
      
      console.log('[CheckoutPage] Order created successfully via RTK Query:', createdOrder);
      
      const orderForConfirmation = {
        ...createdOrder, 
        // estimatedDeliveryDate is UI sugar, can be calculated based on FG details if needed post-creation
        // For now, let's assume it might come from order details or can be omitted here.
        estimatedDeliveryDate: getEstimatedDeliveryDate(createdOrder.fulfillmentGroups?.[0]?.shippingRate) // Example
      };
      
      dispatch(setCurrentOrder(orderForConfirmation));
      dispatch(setOrderId(createdOrder.id)); // This might be redundant if setCurrentOrder has full order
      
      // ---- START CALLING NEW API TO REMOVE ITEMS FROM BACKEND CART ----
      // Prepare purchased items details for the backend API call
      const itemsToRemoveFromBackendCart = cartItems.map(item => ({
        productId: item.product?.id || item.id,
        // item.type should now be correctly populated from the unified cart
        type: item.type, 
        variantId: item.variantId || item.productOptions?.map(opt => opt.value).join('-') || null,
        currency: (item.currency || 'USD').toUpperCase() // Item's own currency
      }));

      console.log('[CheckoutPage] Attempting to remove purchased items from backend cart. Payload:', { purchasedItems: itemsToRemoveFromBackendCart, currency: checkoutCurrency.toUpperCase() });
      await removePurchasedItemsFromCart({ purchasedItems: itemsToRemoveFromBackendCart, currency: checkoutCurrency.toUpperCase() }).unwrap();
      console.log('[CheckoutPage] Successfully called API to remove purchased items from backend cart.');
      // ---- END CALLING NEW API TO REMOVE ITEMS FROM BACKEND CART ----

      dispatch(clearCartState()); // Clear local cart state
      dispatch(resetCheckout()); // Reset checkout state including strapiPaymentId
      
      setPageLoading(false);
      history.push('/checkout/confirmation');

    } catch (err) {
      console.error('[CheckoutPage] Error during post-payment order creation:', err);
      setPageLoading(false);
      const apiErrorMessage = err.data?.message || err.message || 'Payment succeeded, but failed to finalize your order. Please contact support.';
      setPageError(apiErrorMessage); 
      toast.error(apiErrorMessage);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setPageLoading(false);
    if (errorMessage && (
      errorMessage.includes('payment_method.card should be an object') ||
      errorMessage.includes('Stripe has not been properly initialized') ||
      errorMessage.includes('card element')
    )) {
      console.log('Remounting Stripe Elements to fix initialization issues');
      setTimeout(() => {
        setStripeElementsKey(prev => prev + 1);
      }, 500);
    }
    const finalErrorMessage = errorMessage || 'There was an error processing your payment';
    setPageError(finalErrorMessage);
    toast.error(finalErrorMessage);
  };
  
  // Helper function to check if an item is unshippable
  const isItemUnshippable = (item) => {
    const { unshippableItems } = checkoutState;
    const itemId = item.id || item.cartItemIndex;
    const method = item.deliveryMethod || 'shipping';
    
    // Check in the new structure
    if (typeof unshippableItems === 'object' && !Array.isArray(unshippableItems)) {
      // If unshippableItems is the new object structure
      if (unshippableItems[method]) {
        // Check all address groups for this method
        for (const addressId in unshippableItems[method]) {
          if (unshippableItems[method][addressId]?.includes(itemId)) {
            return true; // This item is unshippable
          }
        }
      }
      
      // Also check legacy array for backward compatibility
      if (unshippableItems.legacy && Array.isArray(unshippableItems.legacy)) {
        if (unshippableItems.legacy.includes(itemId)) {
          return true; // This item is unshippable
        }
      }
      
      return false; // Item is shippable
    }
    
    // Fallback to legacy behavior if unshippableItems is still an array
    return Array.isArray(unshippableItems) && unshippableItems.includes(itemId);
  };
  
  // Helper function to get shippable items
  const getShippableItems = () => {
    return cartItems.filter(item => !isItemUnshippable(item));
  };

  const calculateSubtotal = () => {
    const shippableItems = getShippableItems();
    let subtotal = 0;
    
    shippableItems.forEach(item => {
      // Use item.type to determine pricing logic
      if (item.type === 'buy') {
        subtotal += item.price * item.quantity;
      } else if (item.type === 'rent') {
        if (item.rentalPeriod && item.rentalPeriod.price) {
          subtotal += item.rentalPeriod.price * item.quantity;
        // Fallback to older structures if needed, though rentalPeriod.price should be standard
        } else if (item.rentTimeSelect && item.rentTimeSelect.price) { 
          subtotal += item.rentTimeSelect.price * item.quantity;
        } else if (item.rentPriceAndTime && item.rentPriceAndTime.length > 0 && item.rentPriceAndTime[0].price) {
          subtotal += item.rentPriceAndTime[0].price * item.quantity;
        } else {
          // Fallback to item.price if rental specific price isn't found (should be rare)
          subtotal += item.price * item.quantity; 
        }
      } else {
        // Handle other types like 'swap' if they have a price component, or default to item.price
        subtotal += item.price * item.quantity;
      }
    });
    return subtotal;
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const platformFee = subtotal * 0.1;
    return (subtotal + platformFee) * 0.08;
  };

  const calculateTotal = () => {
    // Use the helper function to get shippable items
    const shippableItems = getShippableItems();
    
    let subtotal = 0;
    let shippingCost = checkoutState.selectedShippingRate?.price || 0;
    
    // Only calculate for shippable items
    shippableItems.forEach(item => {
      if (item.type === 'buy') {
        subtotal += item.price * item.quantity;
      } else if (item.type === 'rent') {
        if (item.rentalPeriod && item.rentalPeriod.price) {
          subtotal += item.rentalPeriod.price * item.quantity;
        } else if (item.rentTimeSelect && item.rentTimeSelect.price) {
          subtotal += item.rentTimeSelect.price * item.quantity;
        } else if (item.rentPriceAndTime && item.rentPriceAndTime.length > 0 && item.rentPriceAndTime[0].price) {
          subtotal += item.rentPriceAndTime[0].price * item.quantity;
        } else {
          subtotal += item.price * item.quantity;
        }
      } else {
        subtotal += item.price * item.quantity;
      }
    });
    
    // If no shippable items, return 0
    if (shippableItems.length === 0) {
      return 0;
    }
    
    const platformFee = subtotal * 0.1;
    const tax = (subtotal + platformFee) * 0.08;
    return subtotal + platformFee + tax + shippingCost;
  };
  
  const getVariantText = (item) => {
    let variantText = '';
    if (item.productOptions && item.productOptions.length > 0) {
      variantText = item.productOptions.map(opt => `${opt.name}: ${opt.value}`).join(', ');
    } else if (item.size || item.color) {
      let variants = [];
      if (item.size) variants.push(`Size: ${item.size}`);
      if (item.color) variants.push(`Color: ${item.color}`);
      variantText = variants.join(', ');
    }
    return variantText;
  };
  
  const getEstimatedDeliveryDate = (rate) => {
    if (!rate || rate.days === undefined || rate.days === null) {
      console.log('getEstimatedDeliveryDate: Rate or days missing', rate);
      return 'Date not available'; 
    }
    let deliveryDays = 5; 
    if (typeof rate.days === 'string') {
      const parts = rate.days.split('-');
      const parsedDays = parseInt(parts[parts.length - 1] || parts[0], 10);
      if (!isNaN(parsedDays)) {
        deliveryDays = parsedDays;
      }
    } else if (typeof rate.days === 'number') {
      deliveryDays = rate.days;
    }
    deliveryDays = Math.max(1, Math.round(deliveryDays)); 
    const date = new Date();
    date.setDate(date.getDate() + deliveryDays);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const stripeOptions = {
    appearance: {
      theme: 'stripe',
    },
    loader: 'auto',
  };
  
  const combinedLoading = pageLoading || isCreatingOrder;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="border-b border-gray-200 pb-4 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <BackButton className="text-brand-teal hover:text-brand-teal-dark transition-colors duration-200" />
          </div>
          <h1 className="text-2xl font-bold text-center flex-1 text-gray-800">Checkout</h1>
          <div className="w-24">
          </div>
        </div>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-8 relative"> 
        {combinedLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
            <LoadingSpinner />
          </div>
        )}
        
        <div className={`lg:w-2/3 ${combinedLoading ? 'opacity-50 pointer-events-none' : ''}`}>
          {pageError && (
            <div className="mb-6">
              <ErrorMessage message={pageError} />
            </div>
          )}
          
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Shipping Information</h2>
            <ShippingSection 
              onComplete={handleShippingComplete}
              setError={setPageError}
              disabled={combinedLoading} 
            />
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Payment Information</h2>
            <Elements 
              stripe={memoizedStripePromise} 
              options={stripeOptions} 
              key={stripeElementsKey}
            >
              <PaymentSection 
                disabled={!shippingComplete || combinedLoading} 
                onComplete={handlePaymentComplete}
                onError={handlePaymentError}
                setLoading={setPageLoading}
                amount={calculateTotal()}
              />
            </Elements>
          </div>
        </div>
        
        <div className={`lg:w-1/3 ${combinedLoading ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Order Summary</h2>
            <OrderSummary 
              cartItems={cartItems}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage; 